import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';
import mondaySdk from 'monday-sdk-js';

const monday = mondaySdk();

export type MondayContextState = {
  context: Record<string, unknown> | null;
  isLoading: boolean;
  error: Error | null;
};

const initialMondayContextState: MondayContextState = {
  context: null,
  isLoading: true,
  error: null,
};

const MondaySdkReactContext = createContext<MondayContextState>(initialMondayContextState);

export const MondayContextProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<MondayContextState>(initialMondayContextState);
  useEffect(() => {
    setState((prevState) => ({ ...prevState, isLoading: true, error: null }));
    try {
      monday.listen('context', (res: { data: Record<string, unknown> }) => {
        setState({ context: res.data, isLoading: false, error: null });
      });
    } catch (error) {
      setState((prevState) => ({
        ...prevState,
        isLoading: false,
        error: error instanceof Error ? error : new Error('Failed to load Monday context'),
      }));
    }
  }, []);

  return <MondaySdkReactContext.Provider value={state}>{children}</MondaySdkReactContext.Provider>;
};

export const useMondayContext = (): MondayContextState => {
  const context = useContext(MondaySdkReactContext);
  if (context === undefined) {
    throw new Error('useMondayContext must be used within a MondayContextProvider');
  }
  return context;
};
